import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Alert, Badge, Button, toast } from '@/components/ui'
import { ApiError } from '@/api/problemDetails'

import { useRotateWebChatSecret, useSetWebChatStatus, useUpdateWebChatOrigins } from '../queries'
import type { WebChatChannelSummary } from '../types'

interface WebChatChannelCardProps {
  readonly tenantId: string
  readonly channel: WebChatChannelSummary
  readonly onSecretRotated: (publishableKey: string, signingSecret: string) => void
}

/** Card de um widget: identidade (nome, publishable key, status), edição de origens, rotação do secret e liga/desliga. */
export function WebChatChannelCard({
  tenantId,
  channel,
  onSecretRotated,
}: WebChatChannelCardProps) {
  const { t } = useTranslation()
  const [editingOrigins, setEditingOrigins] = useState(false)
  const [originsText, setOriginsText] = useState(channel.allowedOrigins.join('\n'))

  const updateOrigins = useUpdateWebChatOrigins(tenantId)
  const rotateSecret = useRotateWebChatSecret(tenantId)
  const setStatus = useSetWebChatStatus(tenantId)
  const active = channel.status === 'Active'

  // Sem feedback de erro, o admin não saberia que a ação falhou (CLAUDE.md: "sem fallback
  // silencioso"). Mostramos o erro da última mutação que falhou (React Query zera em novo mutate).
  const actionError = updateOrigins.error ?? rotateSecret.error ?? setStatus.error ?? null

  const saveOrigins = () => {
    updateOrigins.mutate(
      { webChatChannelId: channel.id, allowedOrigins: parseOrigins(originsText) },
      {
        onSuccess: () => {
          setEditingOrigins(false)
          toast.success(t('webchat.toast.originsSaved'))
        },
      },
    )
  }

  const toggleStatus = () => {
    const enabled = !active
    setStatus.mutate(
      { webChatChannelId: channel.id, enabled },
      {
        onSuccess: () => {
          toast.success(enabled ? t('webchat.toast.enabled') : t('webchat.toast.disabled'))
        },
      },
    )
  }

  const rotate = () => {
    rotateSecret.mutate(
      { webChatChannelId: channel.id },
      {
        onSuccess: (result) => {
          onSecretRotated(channel.publishableKey, result.signingSecret)
          // Descartamos a cópia residual do novo segredo no cache da mutação após içá-lo ao pai.
          rotateSecret.reset()
        },
      },
    )
  }

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border-default bg-background-elevated p-5">
      {actionError !== null && (
        <Alert variant="danger" title={t('webchat.card.actionFailedTitle')}>
          {actionError instanceof ApiError
            ? actionError.problem.title
            : t('webchat.card.actionFailedGeneric')}
        </Alert>
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-sm font-semibold text-foreground-primary">
              {channel.name}
            </h3>
            <Badge variant={active ? 'success' : 'neutral'}>
              {active ? t('webchat.card.active') : t('webchat.card.disabled')}
            </Badge>
          </div>
          <code className="mt-1 block truncate font-mono text-xs text-foreground-muted">
            {channel.publishableKey}
          </code>
        </div>
        <Button variant="ghost" size="sm" disabled={setStatus.isPending} onClick={toggleStatus}>
          {active ? t('webchat.card.disable') : t('webchat.card.enable')}
        </Button>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-foreground-secondary">
            {t('webchat.card.originsLabel')}
          </span>
          {!editingOrigins && (
            <button
              type="button"
              onClick={() => {
                setOriginsText(channel.allowedOrigins.join('\n'))
                setEditingOrigins(true)
              }}
              className="text-xs font-medium text-action-primary hover:underline"
            >
              {t('webchat.card.editOrigins')}
            </button>
          )}
        </div>

        {editingOrigins ? (
          <div className="flex flex-col gap-2">
            <textarea
              value={originsText}
              onChange={(e) => {
                setOriginsText(e.target.value)
              }}
              rows={3}
              className="rounded-md border border-border-default bg-background-default px-3 py-2 font-mono text-xs text-foreground-primary outline-none focus:border-action-primary"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setEditingOrigins(false)
                }}
              >
                {t('webchat.card.cancel')}
              </Button>
              <Button size="sm" disabled={updateOrigins.isPending} onClick={saveOrigins}>
                {t('webchat.card.saveOrigins')}
              </Button>
            </div>
          </div>
        ) : channel.allowedOrigins.length > 0 ? (
          <ul className="flex flex-wrap gap-1.5">
            {channel.allowedOrigins.map((origin) => (
              <li
                key={origin}
                className="rounded-md border border-border-default bg-background-subtle px-2 py-1 font-mono text-xs text-foreground-secondary"
              >
                {origin}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-foreground-muted">{t('webchat.card.noOrigins')}</p>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-border-default pt-3">
        <span className="text-xs text-foreground-muted">{t('webchat.card.rotateHint')}</span>
        <Button variant="secondary" size="sm" disabled={rotateSecret.isPending} onClick={rotate}>
          {rotateSecret.isPending ? t('webchat.card.rotating') : t('webchat.card.rotate')}
        </Button>
      </div>
    </div>
  )
}

function parseOrigins(text: string): readonly string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
}
